import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Copy } from "@/assets/icons/ds";
import { useAlert } from "@/hooks/useAlert";
import { AlertTypes } from "@/utils/atoms";
import { parseEsimMatchingId } from "@/utils/esimActivationCode";

interface Props {
  matchingId: string;
}

/**
 * Figma: label above value inside a `surface-tertiary` box, a copy icon on
 * the right - the shared `ClipboardField` is the older bordered-button look
 * used by Friends with Benefits, not this design.
 */
const CodeRow = ({
  dataTestid,
  labelTestid,
  label,
  value,
  successMessage,
  errorMessage,
}: {
  dataTestid: string;
  labelTestid: string;
  label: string;
  value: string;
  successMessage: string;
  errorMessage: string;
}) => {
  const showAlert = useAlert();
  const { t } = useTranslation();

  return (
    <button
      data-testid={dataTestid}
      className="flex w-full items-center justify-between gap-2 rounded-md bg-surface-tertiary p-4 text-left"
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          showAlert({ type: AlertTypes.info, text: successMessage });
        } catch {
          showAlert({ type: AlertTypes.error, text: errorMessage });
        }
      }}
    >
      {/* Spans only: a button may hold phrasing content, not a div. */}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          data-testid={labelTestid}
          className="text-body-xs text-foreground-secondary"
        >
          {label}
        </span>
        <span className="break-words text-body-base font-semibold text-foreground-primary">
          {value}
        </span>
      </span>
      <Copy className="h-6 w-6 shrink-0 text-foreground-primary" aria-hidden />
      <span className="sr-only">{t("clipboardField.copy")}</span>
    </button>
  );
};

/** One copyable row per part of the code; `id` also names its test ids. */
const codeRows = [
  {
    id: "smdp-plus-address",
    part: "smdpPlusAddress",
    labelKey: "smdpPlusAdress",
    successKey: "addressCopied",
    errorKey: "addressCopyError",
  },
  {
    id: "activation-code",
    part: "activationCode",
    labelKey: "activationCode",
    successKey: "codeCopied",
    errorKey: "codeCopyError",
  },
  {
    id: "complete-activation-code",
    part: "completeActivationCode",
    labelKey: "completeActivationCode",
    successKey: "completeCodeCopied",
    errorKey: "completeCodeCopyError",
  },
] as const;

export const RegistrationDetails: FC<Props> = ({ matchingId }) => {
  const parsed = parseEsimMatchingId(matchingId);
  const { t } = useTranslation(undefined, {
    keyPrefix:
      "cockpit.managementTile.details.sections.details.registrationDetails",
  });

  // A malformed code - missing the `$`-separated address/code parts, or
  // either part empty (e.g. "LPA:1$$code") - has nothing sensible to show
  // or copy. Say so rather than render or copy `undefined`/an empty value.
  if (!parsed) {
    return (
      <p
        id="registration-details"
        data-testid="registration-details"
        className="text-body-xs text-foreground-secondary"
      >
        {t("invalidCode")}
      </p>
    );
  }

  return (
    <div
      id="registration-details"
      data-testid="registration-details"
      className="flex flex-col gap-3"
    >
      <p
        data-testid="info-text"
        className="text-body-xs text-foreground-secondary"
      >
        {t("infoText")}
      </p>
      {codeRows.map(({ id, part, labelKey, successKey, errorKey }) => (
        <CodeRow
          key={id}
          dataTestid={`copy-${id}`}
          labelTestid={`${id}-label`}
          label={t(labelKey)}
          value={parsed[part]}
          successMessage={t(successKey)}
          errorMessage={t(errorKey)}
        />
      ))}
    </div>
  );
};
